import { useEffect, useMemo, useRef, useState } from "react";
import { WizardStep } from "./wizardStep";
import { FileUpload, wizardStepActionType, wizardStepType } from "../../type";
import { Footer } from "./footer";
import { Container } from "./container";
import { generateUniqueId, getSheetNames } from "./utility";
import { message } from "../../../../components/toast";
import { INVALID_SHEET_NAME, SELECT_A_FILE, SELECT_FILES, VALID_DESTINATION } from "../../message";
import { useApp } from "../../../../components/app";

const WizardBox = (props: any) => {
  const { api, closeAddUpdate, isUpdate, selectedFunction } = props;
  const app = useApp();
  const [currentStep, setCurrentStep] = useState<wizardStepType>(wizardStepType.BrowserFile);
  const [fileUpload, setFileUpload] = useState<FileUpload[]>([]);
  const [FileUploadRefresh, setFileUploadRefresh] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [resetId, setResetId] = useState<string | null>(null);
  const [columnMappingData, setColumnMappingData] = useState<{ fileIndex: number; mappings: any[] }[]>([]);
  const [dateValidationResults, setDateValidationResults] = useState<any>({});

  const stableDateFormat = useRef<string>("dd/MM/yyyy"); // Default fallback

  useEffect(() => {
    const format = app?.conversation?.company_settings?.dateFormat;
    if (format && !stableDateFormat.current.includes("/")) {
      stableDateFormat.current = format;
      console.log("Date format cached:", stableDateFormat.current);
    }
  }, [app?.conversation?.company_settings?.dateFormat]);


  const onFileUpdateRefresh = (status: boolean) => {
    setFileUploadRefresh(status);
  };

  const onFileUpdate = (updatedFiles: FileUpload[]) => {
    setFileUpload(updatedFiles);
    setRefresh(!refresh);
  };

  const fileNeedFunction = useMemo(() => {
    if (!selectedFunction) {
      return [];
    }
    return app.currentApp?.functionList?.filter(
      (x) => x.functionColumns && x.functionColumns.length > 0 && selectedFunction.find((y: any) => y === x.id)
    ) ?? [];
  }, [selectedFunction?.length]);

  const canGoNext = currentStep !== wizardStepType.ColumnsMap ||
    !dateValidationResults?.isMatchedAsExpected ||
    Object.values(dateValidationResults?.isMatchedAsExpected || {}).every((v: any) => v === true);


  const onStepUpdate = async (actionType: wizardStepActionType) => {
    switch (actionType) {
      case wizardStepActionType.next: {
        if (currentStep < wizardStepType.ReviewAndUpload) {
          switch (currentStep) {
            case wizardStepType.BrowserFile: {
              if (fileUpload.length > 0) {
                if (selectedFunction && selectedFunction.length > 0 && fileUpload.length < fileNeedFunction.length) {
                  message("error", SELECT_FILES.replace("{0}", fileNeedFunction.length.toString()));
                  return;
                }
                setFileUpload(await getSheetNames({ fileUpload, expectedDateFormate: app?.conversation?.company_settings?.dateFormat }));
                onFileUpdateRefresh(false);
                setResetId(null);
                break;
              } else {
                message("error", SELECT_A_FILE);
                return;
              }
            }
            case wizardStepType.FunctionMap: {
              if (fileUpload.length > 0) {
                let invalidMap = fileUpload.filter((x) => {
                  if (!x.selectedSheet || x.selectedSheet.length <= 0) {
                    x.selectedSheet = [{ name: "" }];
                  }
                  if (!x.selectedSheet![0]?.name && x.totalSheet && x.totalSheet.length === 1) {
                    x.selectedSheet[0].name = x.totalSheet[0].name;
                  }
                  return !x.selectedSheet![0]?.name || !x.selectedSheet![0]?.functionCode;
                });
                if (invalidMap && invalidMap.length > 0) {
                  message("error", INVALID_SHEET_NAME);
                  return;
                } else if (fileNeedFunction && fileNeedFunction.length > 0) {
                  let notMapped = fileNeedFunction.filter(
                    (t: any) => !fileUpload.find((x) => x.selectedSheet && x.selectedSheet[0].functionCode === t.functionCode)
                  );
                  if (notMapped && notMapped.length > 0) {
                    message(
                      "error",
                      `You haven't mapped all the required functions. Please make sure to map the following functions: ${notMapped
                        .map((x: any) => x.functionName)
                        .join(",")}`
                    );
                    return;
                  }
                }
                setResetId(null);
                break;
              } else {
                setResetId(null);
                break;
              }
            }
            case wizardStepType.ColumnsMap: {
              if (fileUpload.length > 0) {
                let invalidMap = fileUpload.filter(
                  (x) =>
                    x.selectedSheet![0]?.columnMap &&
                    x.selectedSheet![0]?.columnMap?.filter((y) => y.isRequired && y.Source.toString().trim().length <= 0).length > 0
                );
                if (invalidMap && invalidMap.length > 0) {
                  message(
                    "error",
                    `<table>
                      ${VALID_DESTINATION}
                      ${invalidMap.map((x) => `${x.file.name}<br>`).join("")}
                    </table>`,
                    true
                  );
                  return;
                }

                const dateRes = dateValidationResults?.isMatchedAsExpected;
                if (dateRes && Object.keys(dateRes).length > 0) {
                  const allDatesOk = Object.values(dateRes).every((v: any) => v === true);
                  if (!allDatesOk) {
                    message(
                      "error",
                      // "In the uploaded Excel file one of the dates is not matching with the company setting. Please correct and upload again."
                      "Some of the dates are not matched with the company date format field setting. Please correct and upload again."
                    );
                    return; // Stop navigation
                  }
                }

                const allMappings = fileUpload.map((file, index) => ({
                  fileIndex: index,
                  mappings: file.selectedSheet?.[0]?.columnMap?.map((col: any) => ({
                    sourceColumn: col.Source,
                    destinationColumn: col.DestinationCode || col.DestinationLabel,
                    destinationColumnLabelName: col.DestinationLabel,
                  })) || [],
                }));
                setColumnMappingData(allMappings);
                break;
              } else {
                break;
              }
            }
          }
          setCurrentStep(currentStep + 1);
        }
        break;
      }
      case wizardStepActionType.back: {
        if (currentStep > wizardStepType.BrowserFile) {
          setCurrentStep(currentStep - 1);
        }
        break;
      }
    }
  };

  const onColumnValueReset = async () => {
    setResetId(generateUniqueId());
    setColumnMappingData([]);
  };

  return (
    <div className="wizard-box">
      <WizardStep currentStep={currentStep} />
      <Container
        currentStep={currentStep}
        onFileUpdate={onFileUpdate}
        onFileUpdateRefresh={onFileUpdateRefresh}
        api={api}
        app={app}
        isUpdate={isUpdate}
        closeAddUpdate={closeAddUpdate}
        fileUpload={fileUpload}
        refresh={refresh}
        FileUploadRefresh={FileUploadRefresh}
        resetId={resetId}
        selectedFunction={selectedFunction}
        columnMappingData={columnMappingData}
        setColumnMappingData={setColumnMappingData}
        dateValidationResults={dateValidationResults} // NEW: Pass down
        setDateValidationResults={setDateValidationResults} // NEW: Pass down
        stableDateFormat={stableDateFormat.current}
      />
      <Footer
        currentStep={currentStep}
        onStepUpdate={onStepUpdate}
        onColumnValueReset={onColumnValueReset} />
    </div>
  );
};
export { WizardBox };